import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface CountdownEntry {
  remaining: number;
  isRunning: boolean;
  formatted: string;
  durationSeconds: number;
  storageKey: string;
  startTime: string | null;
  format: string;
}

interface CountdownState {
  [key: string]: CountdownEntry;
}

const initialStorageKeyState: CountdownEntry = {
  remaining: 0,
  isRunning: false,
  formatted: '00:00:00',
  durationSeconds: 0,
  storageKey: 'init_countdown',
  startTime: null,
  format: 'HH:mm:ss',
};

const initialState: CountdownState = {
  init_countdown: initialStorageKeyState,
};

export const countDownSlice = createSlice({
  name: 'countdown',
  initialState,
  reducers: {
    initCountdown(state, action) {
      const {
        durationSeconds,
        storageKey,
        format = initialStorageKeyState.format,
      } = action.payload;
      state[storageKey] = state[storageKey] || { ...initialStorageKeyState };
      state[storageKey].durationSeconds = durationSeconds;
      state[storageKey].storageKey = storageKey || 'init_countdown';

      if (state[storageKey].startTime) {
        const start = moment(state[storageKey].startTime);
        const end = moment(start).add(durationSeconds, 'seconds');
        const now = moment();
        const diff = end.diff(now, 'seconds');

        if (diff > 0) {
          state[storageKey].remaining = diff;
          state[storageKey].isRunning = true;
        } else {
          state[storageKey] = { ...initialStorageKeyState, storageKey };
          state[storageKey].remaining = 0;
        }
      } else {
        state[storageKey].remaining = durationSeconds;
      }

      state[storageKey].formatted = moment.utc(state[storageKey].remaining * 1000).format(format);
    },

    tick(state, action) {
      const { storageKey } = action.payload;
      state[storageKey] = state[storageKey] || { ...initialStorageKeyState, storageKey };

      if (!state[storageKey].isRunning) return;

      const startTime = state[storageKey].startTime;
      if (!startTime) return;

      const start = moment(startTime);
      const end = moment(start).add(state[storageKey].durationSeconds, 'seconds');
      const now = moment();
      const diff = end.diff(now, 'seconds');

      if (diff <= 0) {
        state[storageKey] = { ...initialStorageKeyState, storageKey };
      } else {
        state[storageKey].remaining = diff;
      }

      state[storageKey].formatted = moment
        .utc(state[storageKey].remaining * 1000)
        .format(state[storageKey].format);
    },

    start(state, action) {
      const { storageKey } = action.payload;
      state[storageKey] = state[storageKey] || { ...initialStorageKeyState, storageKey };

      if (state[storageKey].isRunning) return;

      const startTime = moment().toISOString();

      state[storageKey].startTime = startTime;
      state[storageKey].isRunning = true;
    },

    reset(state, action) {
      const { storageKey } = action.payload;

      state[storageKey] = { ...initialStorageKeyState, storageKey };
      state[storageKey].formatted = moment
        .utc(state[storageKey].remaining * 1000)
        .format(state[storageKey].format);
    },
  },
});

export const { initCountdown, tick, start, reset } = countDownSlice.actions;

export default countDownSlice.reducer;
